<template>
    <div>
        <v-card flat style="border-top: 1px solid lightgray">
            <v-overlay :value="loadingExportInvoiceList">
                <v-progress-circular indeterminate size="64"></v-progress-circular>
            </v-overlay>
            <v-data-table
                    :headers="exportInvoiceTableHeaders"
                    :items="exportInvoices"
                    :options.sync="options"
                    dense
                    disable-pagination
                    group-by="category"
                    group-desc
                    group-key="category"
                    hide-default-footer
                    item-key="id"
                    :custom-sort="customSort"
            >
                <template
                        v-for="h in exportInvoiceTableHeaders"
                        v-slot:[`header.${h.value}`]="{ header }"
                        :class="'text-' + header.align"
                >
                    {{ header.text ? t(header.text) : "-" }}
                </template>
                <template v-slot:group.header="{ group, headers, toggle, isOpen, item }">
                    <td
                            v-for="(header, index) in headers"
                            :key="header.value"
                            :colspan="index === 0 ? 2 : 1"
                            class="font-weight-bold"
                            v-if="index !== 3"
                    >
                        <span v-if="index === 0">
                            <v-btn v-if="index === 0" :ref="group" :data-open="isOpen" icon small
                                   @click="handleToggle(group, isOpen, toggle)">
                                <v-icon v-if="isOpen">arrow_drop_down</v-icon>
                                <v-icon v-else>arrow_drop_up</v-icon>
                            </v-btn>
                            {{ getGroupName(group) }}
                        </span>

                        <span v-if="header.value === 'total_net' && !isOpen" class="font-weight-bold">
                            {{
                                calculatedValues.find(({ category }) => category === group)
                                        ? rn(calculatedValues.find(({ category }) => category === group).total_net, 2)
                                        : "-"
                            }}
                        </span>
                        <span v-if="header.value === 'total_arrived' && !isOpen" class="font-weight-bold">
                            {{
                                calculatedValues.find(({ category }) => category === group)
                                        ? rn(calculatedValues.find(({ category }) => category === group).total_arrived, 2)
                                        : "-"
                            }}
                        </span>
                        <span v-if="header.value === 'total_profit' && !isOpen" class="font-weight-bold">
                            {{
                                calculatedValues.find(({ category }) => category === group)
                                        ? rn(calculatedValues.find(({ category }) => category === group).total_profit, 2)
                                        : "-"
                            }}
                        </span>
                        <span v-if="header.value === 'percent' && !isOpen" class="font-weight-bold">
                            {{
                                calculatedValues.find(({ category }) => category === group)
                                        ? rn(calculatedValues.find(({ category }) => category === group).percent, 2)
                                        : "-"
                            }}
                        </span>
                    </td>
                </template>

                <template v-slot:group.summary="{ group, headers, isOpen }">
                    <td v-for="h in exportInvoiceTableHeaders" :key="h.value" v-if="isOpen">
                        <span v-if="h.value === 'total_net'" class="font-weight-bold">
                            {{
                                calculatedValues.find(({ category }) => category === group)
                                        ? rn(calculatedValues.find(({ category }) => category === group).total_net, 2)
                                        : "-"
                            }}
                        </span>
                        <span v-else-if="h.value === 'total_profit'" class="font-weight-bold">
                            {{
                                calculatedValues.find(({ category }) => category === group)
                                        ? rn(calculatedValues.find(({ category }) => category === group).total_profit, 2)
                                        : "-"
                            }}
                        </span>
                        <span v-else-if="h.value === 'total_arrived'" class="font-weight-bold">
                            {{
                                calculatedValues.find(({ category }) => category === group)
                                        ? rn(calculatedValues.find(({ category }) => category === group).total_arrived, 2)
                                        : "-"
                            }}
                        </span>
                        <span v-else-if="h.value === 'percent'" class="font-weight-bold">
                            {{
                                calculatedValues.find(({ category }) => category === group)
                                        ? rn(calculatedValues.find(({ category }) => category === group).percent, 2)
                                        : "-"
                            }}
                        </span>
                    </td>
                </template>

                <template v-slot:top>
                    <export-sale-invoice-clone-dialog v-if="exportInvoiceCloneDialog"/>
                </template>
                <template v-slot:item="{ item, expand, group, toggle }">
                    <tr
                            :key="'invoice_' + item.id"
                            :class="{
                            activeTableRow:
                                activeExportInvoice && parseInt(item.id) === parseInt(activeExportInvoice.id),
                        }"
                            @click="handleTableRowClick($event, item)"
                    >
                        <td
                                v-for="(row, index) in exportInvoiceTableHeaders"
                                :class="{
                                lastColumn:
                                    index === exportInvoiceTableHeaders.length - 1 &&
                                    exportInvoiceTableHeaders.length > 2,
                                penultimateColumn:
                                    index === exportInvoiceTableHeaders.length - 2 &&
                                    exportInvoiceTableHeaders.length > 2,
                            }"
                        >
                            <span v-if="row.value === 'percent'" @click="handleIsActiveClick(item)">
                                <v-edit-dialog
                                        v-if="
                                        !item['is_active'] &&
                                        hasPermissionForAction(PermissionsConstants.EXPORT, PermissionsConstants.EDIT)
                                    "
                                        :return-value.sync="item['percent']"
                                        @save="handlePercentChange(item['percent'], item)"
                                >
                                    {{ item["percent"] ? item["percent"] + " %" : "-" }}
                                    <template v-slot:input>
                                        <v-text-field
                                                v-model="item['percent']"
                                                :label="'Percent' | t"
                                                append-icon="fas fa-percentage"
                                                counter
                                                single-line
                                                @keypress="onlyIntegersWithComa"
                                        ></v-text-field>
                                    </template>
                                </v-edit-dialog>
                                <span v-else>{{ item.percent ? item.percent + " %" : "-" }}</span></span
                            >
                            <span v-else-if="row.value === 'is_active'" @click="handleIsActiveClick(item)">
                                <v-icon v-if="item['is_active']" color="green" small>fal fa-check-square</v-icon>
                                <v-icon v-else color="red" small>fal fa-minus-square</v-icon></span
                            >
                            <span v-else-if="row.value === 'arrival'">{{ countDays(item["arrival_date"]) }}</span>
                            <span v-else-if="row.value === 'actions'" class="text-end">
                                <v-icon
                                        small
                                        @click="handleDuplicateClick(item)"
                                        v-if="
                                        hasPermissionForAction(PermissionsConstants.EXPORT, PermissionsConstants.CREATE)
                                    "
                                >fa fa-clone</v-icon
                                >
                                <v-icon
                                        class="ml-1"
                                        small
                                        @click="handleDeleteClick(item)"
                                        v-if="
                                        hasPermissionForAction(PermissionsConstants.EXPORT, PermissionsConstants.DELETE)
                                    "
                                >fas fa-trash-alt
                                </v-icon></span
                            >
                            <span v-else-if="row.value === 'payroll_date'">{{
                                    getPayrollDate(item.payroll_date)
                                }}</span>
                            <span v-else-if="row.value === 'invoice_date'">{{
                                    getPayrollDate(item.invoice_date)
                                }}</span>
                            <span v-else-if="row.value === 'client_country'">{{ getCountryName(item) }}</span>
                            <span v-else-if="row.value === 'flag'"
                            ><country-flag v-if="item.country_code" :country="item.country_code" size="small"
                            /></span>
                            <span v-else-if="row.value === 'marginal'">
                                {{
                                    item.total_profit && item.total_net
                                            ? (Math.round((item.total_profit / item.total_net) * 100 * 100) / 100).toFixed(
                                            2
                                    ) + "%"
                                            : "-"
                                }}
                            </span>
                            <span v-else>{{ getRowValue(item, row) }}</span>
                        </td>
                    </tr>
                </template>
            </v-data-table>
        </v-card>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import Store from "../../store";
import DatePicker from "v-calendar/lib/components/date-picker.umd";
import ExportSaleInvoiceCloneDialog from "../export-sale-invoice/export-sale-invoice-clone-dialog";
import moment from "moment";
import CountryFlag from "vue-country-flag";
import { PermissionsConstants } from "@/permissions";

export default {
    name: "export-sale-invoice-list",
    components: {
        ExportSaleInvoiceCloneDialog,
        DatePicker,
        CountryFlag,
    },
    data() {
        return {
            search: null,
            initialLoad: false,
            footerProps: { "items-per-page-options": [25, 50, 100] },
            options: this.tableOptions,
            handleDeleteClickBool: false,
            handleApprovedClickBool: false,
        };
    },
    async mounted() {
        if (this.drawerOpen) {
            document.documentElement.style.overflow = "hidden";
        }
        await this.getFrequentlyUsedQueries({ users: true, products: true, export_clients: true });

        this.handleScroll = this.debounce(() => {
            this.updateScrollPosition();
        }, 300);
        window.addEventListener("scroll", this.handleScroll);
    },
    beforeDestroy() {
        window.removeEventListener("scroll", this.handleScroll);
    },
    watch: {
        options(val) {
            Store.commit("ExportSaleInvoiceStore/setTableOptions", val);
        },
        drawerOpen() {
            if (this.drawerOpen) {
                document.documentElement.style.overflow = "hidden";
            } else {
                document.documentElement.style.overflow = "auto";
            }
        },
        "$route.name": {
            handler(newRoute, oldRoute) {
                const doScroll = !["export-sale-invoice-view", "export-sale-invoice-edit"].includes(oldRoute);
                if (newRoute === "export-sale-invoice-list") {
                    this.$nextTick(() => {
                        this.handlePageLoad(doScroll);
                    });
                }
            },
            immediate: true,
        },
        "$route.query.scrollToMonth": {
            handler(newScrollToMonth) {
                if (newScrollToMonth && this.initialLoad) {
                    this.$nextTick(() => {
                        this.handlePageLoad(true, false);
                    });
                }
            },
        },
        exportInvoices: {
            handler(newInvoices, oldInvoices) {
                if (newInvoices.length > 0 && !this.initialLoad) {
                    this.$nextTick(() => {
                        this.handlePageLoad();
                        this.initialLoad = true;
                    });
                }
            },
            immediate: true,
        },
    },
    computed: {
        ...mapState("ExportSaleInvoiceStore", [
            "exportInvoices",
            "loadingExportInvoiceList",
            "activeExportInvoice",
            "drawerOpen",
            "calculatedValues",
            "exportInvoiceCloneDialog",
            "exportInvoiceTableHeaders",
        ]),
        ...mapState(["countries"]),
        ...mapGetters(["hasPermissionForAction"]),
        PermissionsConstants() {
            return PermissionsConstants;
        },
    },
    methods: {
        ...mapActions(["getFrequentlyUsedQueries"]),
        cancel() {
            Store.commit("ExportSaleInvoiceStore/setSnackbar", { text: "Canceled", color: "error" });
        },
        getExpandedGroups() {
            const expanded = this.$route.query.expanded;
            return expanded ? expanded.split(",").filter(key => key) : [];
        },
        handleDuplicateClick(invoice) {
            Store.commit("ExportSaleInvoiceStore/setActiveExportInvoice", invoice);
            Store.commit("ExportSaleInvoiceStore/setExportInvoiceCloneDialog", true);
        },
        handleDeleteClick(invoice) {
            this.handleDeleteClickBool = true;
            if (!invoice.is_active) {
                if (confirm("Are you sure you want to delete this invoice?")) {
                    Store.dispatch("ExportSaleInvoiceStore/deleteInvoice", { invoice_id: invoice.id });
                }
            } else {
                alert(this.t("Invoice is locked. Please unlock first."));
            }
            setTimeout(() => {
                this.handleDeleteClickBool = false;
            }, 500);
        },
        getGroupName(groupName) {
            const pattern = /(\d{4})\-(\d{1,2})\-([A-Z]\w*)/;
            const regexDate = pattern.exec(groupName);
            if (!regexDate) {
                return this.t(groupName);
            }
            return `${ this.t(regexDate[3]) } ${ regexDate[1] }`;
        },
        handleTableRowClick(e, invoice) {
            if (!this.hasPermissionForAction(PermissionsConstants.EXPORT, PermissionsConstants.VIEW)) {
                return;
            }

            if (
                    (e.target.localName === "td" || e.target.localName === "span") &&
                    !this.handleApprovedClickBool &&
                    !this.handleDeleteClickBool
            ) {
                if (this.$route.name !== "export-sale-invoice-view") {
                    this.$router.push({ name: "export-sale-invoice-view", params: { exportInvoiceId: invoice.id } });
                    return;
                }
                if (
                        this.$route.name === "export-sale-invoice-view" &&
                        parseInt(invoice.id) !== parseInt(this.$route.params.exportInvoiceId)
                ) {
                    this.$router.push({ name: "export-sale-invoice-view", params: { exportInvoiceId: invoice.id } });
                    Store.dispatch("ExportSaleInvoiceStore/getExportInvoiceData", invoice.id);
                }
            }
        },
        countDays(arrived) {
            if (arrived) {
                let todaysDate = moment(new Date());
                let given = moment(arrived, "DD.MM.YYYY");
                return (given.diff(todaysDate, "days") + 1) * -1;
            } else return "-";
        },
        getPayrollDate(value) {
            if (value)
                return (
                        this.t(moment(value, "DD.MM.YYYY").format("MMM")) + " " + moment(value, "DD.MM.YYYY").format("YYYY")
                );
            else return "-";
        },
        async handleIsActiveClick(invoice) {
            if (!this.hasPermissionForAction(PermissionsConstants.EXPORT, PermissionsConstants.EDIT)) {
                return;
            }

            this.handleApprovedClickBool = true;
            let word = invoice.is_active ? "unlock" : "lock";
            if (confirm("Are you sure you want to " + word + " this invoice?")) {
                await Store.dispatch("ExportSaleInvoiceStore/getExportInvoiceData", invoice.id);
                await Store.commit("ExportSaleInvoiceStore/updateActiveExportInvoiceProperty", {
                    prop: "is_active",
                    value: !this.activeExportInvoice.is_active,
                });
                await Store.dispatch("ExportSaleInvoiceStore/sendExportInvoice", this.activeExportInvoice);
                await Store.commit("ExportSaleInvoiceStore/resetActiveExportInvoice");
            }
            setTimeout(() => {
                this.handleApprovedClickBool = false;
            }, 500);
        },
        async handlePercentChange(value, invoice) {
            value = !value ? null : value.replace(",", ".");
            await Store.dispatch("ExportSaleInvoiceStore/getExportInvoiceData", invoice.id);
            await Store.commit("ExportSaleInvoiceStore/updateActiveExportInvoiceProperty", {
                prop: "percent",
                value: value,
            });
            await Store.dispatch("ExportSaleInvoiceStore/sendExportInvoice", this.activeExportInvoice);
            if (this.$route.name !== "export-sale-invoice-list") {
                await this.$router.push({ name: "export-sale-invoice-list" });
            }
        },
        onlyIntegersWithComa($event) {
            let keyCode = $event.keyCode ? $event.keyCode : $event.key;
            if (keyCode < 44 || keyCode === 47 || keyCode === 45 || keyCode > 57) {
                $event.preventDefault();
            }
        },
        getRowValue(item, row) {
            if (item.data && item.data[row.value] != null) {
                if (typeof item.data[row.value] === "number") return this.rn(item.data[row.value]);
                return item.data[row.value];
            } else if (item[row.value] != null) {
                if (typeof item[row.value] === "number") return this.rn(item[row.value]);
                return item[row.value];
            } else {
                return "-";
            }
        },
        getFlagCode(country) {
            let c = this.countries.find((x) => x.name === country);
            return c ? c.code : false;
        },
        getCountryName(item) {
            if (item.country_estonian) {
                return item.country_estonian;
            }
            if (item.country_english) {
                return item.country_english;
            }
            return "-";
        },
        updateScrollPosition() {
            const scrollY = Math.round(window.scrollY);
            if (scrollY > 0) {
                this.$router.replace({
                    query: {
                        ...this.$route.query,
                        scrollY: scrollY,
                    },
                }).catch(() => {
                });
            } else if (this.$route.query.scrollY) {

                const { scrollY, ...otherQuery } = this.$route.query;
                this.$router.replace({
                    query: otherQuery,
                }).catch(() => {
                });
            }
        },
        debounce(func, wait) {
            let timeout;
            return function executedFunction(...args) {
                const later = () => {
                    clearTimeout(timeout);
                    func(...args);
                };
                clearTimeout(timeout);
                timeout = setTimeout(later, wait);
            };
        },
        handleToggle: function (group, isOpen, toggle) {
            toggle();
            this.debounce(() => {
                const expandedGroups = this.getExpandedGroups();
                if (isOpen) {
                    const index = expandedGroups.indexOf(group);
                    if (index > -1) {
                        expandedGroups.splice(index, 1);
                    }
                } else {
                    if (!expandedGroups.includes(group)) {
                        expandedGroups.push(group);
                    }
                }
                this.$router.replace({
                    query: {
                        ...this.$route.query,
                        expanded: expandedGroups.join(","),
                    },
                }).catch(() => {
                });
            }, 300)();
        },
        handlePageLoad(doScroll = true, manageGroups = true) {
            if (!this.exportInvoices) return;
            const now = new Date();
            const currentMonthKey = `${ now.getFullYear() }-${ String(now.getMonth() + 1).padStart(2, "0") }`;
            const prevMonthKey = `${ now.getFullYear() }-${ String(now.getMonth()).padStart(2, "0") }`;
            const expandedGroups = this.getExpandedGroups();
            const scrollY = parseInt(this.$route.query.scrollY) || 0;
            const scrollToMonthQuery = this.$route.query.scrollToMonth;
            const targetMonthKey = scrollToMonthQuery || currentMonthKey;

            this.$nextTick(() => {
                if (manageGroups) {
                    Object.keys(this.$refs).forEach((k) => {
                        const refItem = Array.isArray(this.$refs[k]) ? this.$refs[k][0] : this.$refs[k];
                        const shouldBeOpen =
                                k >= prevMonthKey ||
                                k === targetMonthKey ||
                                expandedGroups.includes(k);
                        if (
                                refItem &&
                                refItem.$el &&
                                "data-open" in refItem.$attrs &&
                                refItem.$attrs["data-open"] !== shouldBeOpen
                        ) {
                            refItem.$el.click();
                        }
                    });
                }

                if (scrollToMonthQuery) {
                    this.scrollToCurrentMonth(targetMonthKey);
                } else if (doScroll && scrollY) {
                    const pageHeight = document.documentElement.scrollHeight - window.innerHeight;
                    if (scrollY <= pageHeight) {
                        window.scrollTo(0, scrollY);
                    } else {
                        this.scrollToCurrentMonth(targetMonthKey);
                    }
                } else if (doScroll && !scrollY) {
                    this.scrollToCurrentMonth(targetMonthKey);
                }
            });
        },
        scrollToCurrentMonth(currentMonthKey) {
            this.$nextTick(() => {
                const sortedKeys = Object.keys(this.$refs).sort();
                const matchingKey = sortedKeys.find((k) => k >= currentMonthKey);
                if (matchingKey) {
                    const groupRef = Array.isArray(this.$refs[matchingKey])
                            ? this.$refs[matchingKey][0]
                            : this.$refs[matchingKey];

                    if (groupRef && groupRef.$el) {
                        const rect = groupRef.$el.getBoundingClientRect();
                        const headerOffset = 64;
                        const isVisible = rect.top >= headerOffset && rect.bottom <= window.innerHeight;

                        if (!isVisible) {
                            const scrollTop = window.pageYOffset + rect.top - headerOffset;
                            window.scrollTo({ top: scrollTop, behavior: "smooth" });
                        }
                    }
                }
            });
        },
        customSort(items, sortBy, sortDesc) {

            if (sortBy.length !== 2 || !items.length) {
                return items;
            }

            const sortKey = sortBy[1];
            const isDesc = sortDesc[1] || false;

            const groupedItems = {};
            items.forEach(item => {
                const category = item.category || "";
                if (!groupedItems[category]) {
                    groupedItems[category] = [];
                }
                groupedItems[category].push(item);
            });

            const categories = Object.keys(groupedItems).sort((a, b) => b.localeCompare(a));

            const sortedItems = [];

            categories.forEach(category => {
                const groupItems = groupedItems[category].slice().sort((a, b) => {
                    let valueA, valueB;

                    try {
                        if (sortKey === "flag") {
                            valueA = a.country_code || "";
                            valueB = b.country_code || "";
                        } else if (sortKey === "client_country") {
                            valueA = this.getCountryName(a) === "-" ? "" : this.getCountryName(a);
                            valueB = this.getCountryName(b) === "-" ? "" : this.getCountryName(b);
                        } else if (sortKey === "marginal") {
                            valueA = a.total_profit && a.total_net && a.total_net !== 0
                                    ? (a.total_profit / a.total_net) * 100
                                    : -Infinity;
                            valueB = b.total_profit && b.total_net && b.total_net !== 0
                                    ? (b.total_profit / b.total_net) * 100
                                    : -Infinity;
                        } else if (sortKey === "arrival") {
                            valueA = this.countDays(a.arrival_date);
                            valueB = this.countDays(b.arrival_date);
                            valueA = valueA === "-" ? -Infinity : parseInt(valueA);
                            valueB = valueB === "-" ? -Infinity : parseInt(valueB);
                        } else {
                            valueA = a[sortKey] != null ? a[sortKey] : "";
                            valueB = b[sortKey] != null ? b[sortKey] : "";
                        }

                        if (valueA === valueB) return 0;
                        if (valueA === "" || valueA === -Infinity) return isDesc ? -1 : 1;
                        if (valueB === "" || valueB === -Infinity) return isDesc ? 1 : -1;

                        if (typeof valueA === "number" && typeof valueB === "number") {
                            return isDesc ? valueB - valueA : valueA - valueB;
                        }
                        return isDesc
                                ? String(valueB).localeCompare(String(valueA))
                                : String(valueA).localeCompare(String(valueB));
                    } catch (error) {
                        return 0;
                    }
                });
                sortedItems.push(...groupItems);
            });

            return sortedItems;
        },
    },
};
</script>

<style scoped>
.lastColumn {
    text-align: center;
}

.penultimateColumn {
    text-align: end;
}

.activeTableRow {
    background-color: #f41823;
    color: #fff;
}

.activeTableRow:hover {
    background-color: #e11520 !important;
    color: #fff !important;
}

.dot {
    margin-top: 4px;
    height: 15px;
    width: 15px;
    border-radius: 50%;
    display: inline-block;
}

.preventscroll {
    overflow-y: scroll;
    width: 100%;
}

.v-row-group__summary {
    background-color: #b1b1b1 !important;
    height: 40px;
}

::v-deep .v-data-table-header th {
    white-space: nowrap;
}

td {
    height: 25px !important;
}
</style>
